import type {
    ChecklistData,
    ChecklistLabel,
    ChecklistObject,
    ChecklistToc,
    ChecklistTocElement,
    LabelGroup,
    RawChecklistResponse,
    ResourceType,
} from "./types";
import {ChecklistItemType} from "./types";

// --- Helpers ---

const validFileTypes: ResourceType[] = [
    "8" as ResourceType, // pdf
    "1" as ResourceType, // doc
    "2" as ResourceType, // docx
    "4" as ResourceType, // xls
    "5" as ResourceType, // xlsx
    "7" as ResourceType, // pptx
    "15" as ResourceType, // xltm
    "16" as ResourceType, // xltx
    "17" as ResourceType, // xlsm
];

const isValidFileType = (resourceTypeId?: ResourceType): boolean => {
    if (!resourceTypeId) return false;
    const resourceTypeIdString = resourceTypeId.toString() as ResourceType;
    return validFileTypes.includes(resourceTypeIdString);
};

const getLabel = (
    labelId: string,
    objects: Record<string, ChecklistObject>
): ChecklistLabel => ({
    id: labelId,
    name: objects[labelId].name,
});

const generateFilterGroups = (
    labelIds: string[],
    objects: Record<string, ChecklistObject>
): LabelGroup[] => {
    const groupMap = new Map<string, string[]>();

    const labelObjects = labelIds.map((id) => objects[id]);
    labelObjects.sort((a, b) => a.name.localeCompare(b.name));

    labelObjects.forEach((label) => {
        if (label.groupId) {
            const labels = groupMap.get(label.groupId) || [];
            labels.push(label.id);
            groupMap.set(label.groupId, labels);
        }
    });

    return Array.from(groupMap.entries())
        .map(([id, labels]) => ({
            id,
            name: objects[id].name,
            labels: labels.map((labelId) => getLabel(labelId, objects)),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
};

const addInheritedShowFilters = (
    element: ChecklistTocElement,
    toc: ChecklistToc,
    showFilters: string[] = []
) => {
    const newShowFilters = new Set<string>([
        ...showFilters,
        ...(element.show || []),
    ]);
    element.show = Array.from(newShowFilters);
    if (element.children && element.children.length > 0) {
        element.children.forEach((child) => {
            addInheritedShowFilters(toc[child], toc, element.show);
        });
    }
};

const tocTransformer = (
    toc: ChecklistToc = {},
    objects: Record<string, ChecklistObject> = {}
): ChecklistToc => {
    // Defensive shallow clone: this transform mutates element fields in place
    // (show, files, related, viewByGroup, showByGroup, checkItemType, info),
    // but the input may come from a frozen RTK Query cache.
    const mutableToc: ChecklistToc = {};
    for (const [key, element] of Object.entries(toc)) {
        mutableToc[key] = {...element};
    }

    const rootElement = Object.entries(mutableToc).find(([, el]) => el.root);
    if (rootElement) {
        addInheritedShowFilters(mutableToc[rootElement[0]], mutableToc);
    }

    const ret = Object.keys(mutableToc).reduce<ChecklistToc>((accumulator, current) => {
        const element = mutableToc[current];

        // Validate files: cross-reference files vs relations
        const fileRelations = (element.files || [])
            .map((file) =>
                (element.related || []).find(
                    (relation) => relation.topicId === file.topicId
                )
            )
            .filter((fr) => !!fr);

        const topicIsFile = fileRelations.reduce<Record<string, boolean>>(
            (acc, current) => {
                const {topicId, resourceTypeId} = current;
                return {
                    ...acc,
                    [topicId]: isValidFileType(resourceTypeId as ResourceType),
                };
            },
            {}
        );

        element.files = (element.files || []).filter(
            (file) => topicIsFile[file.topicId]
        );

        // Keep all relations (including non-file types for link-style display)
        element.related = element.related || [];

        // Pre-compute viewByGroup
        if (element.view && element.view.length > 0) {
            const viewByGroup: Record<string, string[]> = {};
            element.view.forEach((labelId) => {
                const labelObject = objects[labelId];
                if (labelObject && labelObject.groupId) {
                    if (!viewByGroup[labelObject.groupId]) {
                        viewByGroup[labelObject.groupId] = [];
                    }
                    viewByGroup[labelObject.groupId].push(labelId);
                }
            });
            element.viewByGroup = viewByGroup;
        }

        // Pre-compute showByGroup
        if (element.show && element.show.length > 0) {
            const showByGroup: Record<string, string[]> = {};
            element.show.forEach((labelId) => {
                const labelObject = objects[labelId];
                if (labelObject && labelObject.groupId) {
                    if (!showByGroup[labelObject.groupId]) {
                        showByGroup[labelObject.groupId] = [];
                    }
                    showByGroup[labelObject.groupId].push(labelId);
                }
            });
            element.showByGroup = showByGroup;
        }

        // Determine checkItemType and info status
        if ((element.children || []).length > 0) {
            const isInfo = (element.children || []).reduce<boolean>(
                (acc, childId) => {
                    if (
                        element.type ===
                        (ChecklistItemType.CheckItemInfo as ChecklistTocElement["type"])
                    ) {
                        return true;
                    }
                    if (acc === false) return false;
                    const child = mutableToc[childId];
                    if (
                        child.type !==
                        (ChecklistItemType.CheckItemInfo as ChecklistTocElement["type"])
                    ) {
                        return false;
                    }
                    return acc;
                },
                true
            );
            element.checkItemType = ChecklistItemType.CheckItemParent;
            element.info = isInfo;
            return {...accumulator, [current]: element};
        }

        if (
            !element.children?.length &&
            element.type ===
                (ChecklistItemType.CheckItem as ChecklistTocElement["type"])
        ) {
            element.checkItemType = ChecklistItemType.CheckItem;
            return {...accumulator, [current]: element};
        }

        if (
            !element.children?.length &&
            element.type ===
                (ChecklistItemType.CheckItemInfo as ChecklistTocElement["type"])
        ) {
            element.checkItemType = ChecklistItemType.CheckItemInfo;
            element.info = true;
            return {...accumulator, [current]: element};
        }

        return {...accumulator, [current]: element};
    }, {});

    return ret;
};

// --- Main transform ---

export const checklistFrom = (
    response: RawChecklistResponse
): ChecklistData => {
    const {
        id,
        topicId,
        name,
        version,
        access,
        resourceTypeId,
        publish,
        supplierId,
        supplier,
        topictypeId,
        topictype,
        language,
        collectionId,
        ...rest
    } = response;

    const viewGroups = generateFilterGroups(
        rest.viewroot || [],
        rest.objects || {}
    );

    const showGroups = generateFilterGroups(
        rest.showroot || [],
        rest.objects || {}
    );

    const toc = tocTransformer(rest.toc || {}, rest.objects || {});

    // Find root node
    const rootNodeId =
        Object.entries(toc).find(([, element]) => element.root)?.[0] || "";

    // Build parent map (childId -> parentId)
    const tocParentMap: Record<string, string> = {};
    Object.entries(toc).forEach(([nodeId, element]) => {
        if (element.children) {
            element.children.forEach((childId) => {
                tocParentMap[childId] = nodeId;
            });
        }
    });

    // Compute info nodes (including inherited info status)
    const infoIds: string[] = [];

    const collectInfoIds = (nodeId: string, inheritedInfo = false) => {
        const node = toc[nodeId];
        if (!node) return;

        const isInfo =
            inheritedInfo ||
            node.info === true ||
            node.checkItemType === ChecklistItemType.CheckItemInfo;

        if (isInfo) {
            infoIds.push(nodeId);
        }

        if (node.children) {
            node.children.forEach((childId) => collectInfoIds(childId, isInfo));
        }
    };

    if (rootNodeId) {
        collectInfoIds(rootNodeId);
    }

    // Compute leaf nodes (no children)
    const leafNodeIds: string[] = [];
    Object.entries(toc).forEach(([nodeId, element]) => {
        if (!element.children || element.children.length === 0) {
            leafNodeIds.push(nodeId);
        }
    });

    return {
        metadata: {
            id,
            topicId,
            name,
            version,
            access,
            resourceTypeId,
            publish,
            supplierId,
            supplier,
            topictypeId,
            topictype,
            language,
            collectionId,
        },
        toc,
        objects: rest.objects || {},
        elements: rest.elements || {},
        viewroot: rest.viewroot || [],
        showroot: rest.showroot || [],
        labels: (rest.labels || []) as string[],
        defaultLabels: rest.defaultLabels || [],
        viewGroups,
        showGroups,
        defaultCollectionId: rest.defaultCollectionId,
        rootNodeId,
        tocParentMap,
        infoIds,
        leafNodeIds,
    };
};
